import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  DRIVE_MODES,
  DriveMode,
  FREEZE_LABELS,
  MODE_LABELS,
  OutreachConversationRowDto,
  OutreachOverviewDto,
  STATE_LABELS,
  outreachError,
} from '../../data-transfer-objects/outreach.dto';
import { OutreachService } from '../../services/outreach.service';
import { CopilotPrefill, OutreachCopilotComponent } from './copilot/outreach-copilot.component';
import { OutreachIntelComponent } from './intel/outreach-intel.component';
import { OutreachSimulatorComponent } from './simulator/outreach-simulator.component';

export type OutreachTab = 'copilot' | 'conversations' | 'intel' | 'simulator';
const TABS: OutreachTab[] = ['copilot', 'conversations', 'intel', 'simulator'];

/**
 * People → Outreach: Sharon, the customer-success persona that talks to
 * chosen members in the app chat, and the copilot that helps choose them.
 * See docs/plans/outreach-autodrive.md.
 *
 * The header is what needs a person now (frozen conversations, drafts waiting,
 * overdue intel, proposals); the tabs are where to act on it. A tab, once
 * opened, stays alive while hidden, so a copilot turn that takes two minutes
 * is not lost by looking at the intel meanwhile.
 */
@Component({
  selector: 'app-outreach',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatProgressSpinnerModule,
    OutreachCopilotComponent,
    OutreachIntelComponent,
    OutreachSimulatorComponent,
  ],
  templateUrl: './outreach.component.html',
  styleUrls: ['./outreach.component.scss'],
})
export class OutreachComponent implements OnInit {
  readonly modes = DRIVE_MODES;
  readonly modeLabels = MODE_LABELS;
  readonly stateLabels = STATE_LABELS;

  overview: OutreachOverviewDto | null = null;
  overviewError: string | null = null;

  tab: OutreachTab = 'copilot';
  visited = new Set<OutreachTab>();
  prefill: CopilotPrefill | null = null;

  conversations: OutreachConversationRowDto[] = [];
  modeFilter: DriveMode | '' = '';
  loadingConversations = false;
  conversationsError: string | null = null;

  constructor(private outreach: OutreachService, private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    const asked = this.route.snapshot.queryParamMap.get('tab') as OutreachTab | null;
    this.show(asked && TABS.includes(asked) ? asked : 'copilot', false);
    this.outreach.overview().subscribe({
      next: (o) => {
        this.overview = o;
        if (this.tab === 'simulator' && !o.simulatorAvailable) this.show('copilot');
      },
      error: (err) => (this.overviewError = outreachError(err, 'Could not load the overview')),
    });
  }

  get tabs(): { id: OutreachTab; label: string; count?: number }[] {
    const c = this.overview?.counts;
    const tabs: { id: OutreachTab; label: string; count?: number }[] = [
      { id: 'copilot', label: 'Copilot', count: c?.pending_proposals },
      { id: 'conversations', label: 'Conversations', count: c ? c.pending_drafts + c.frozen : undefined },
      { id: 'intel', label: 'Intel', count: c ? c.overdue_intel + c.new_intel : undefined },
    ];
    if (this.overview?.simulatorAvailable) tabs.push({ id: 'simulator', label: 'Simulator' });
    return tabs;
  }

  show(tab: OutreachTab, updateUrl = true): void {
    this.tab = tab;
    this.visited.add(tab);
    if (tab === 'conversations' && !this.conversations.length) this.loadConversations();
    if (updateUrl) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { tab }, replaceUrl: true });
    }
  }

  setModeFilter(mode: DriveMode | ''): void {
    this.modeFilter = mode;
    this.loadConversations();
  }

  loadConversations(): void {
    this.loadingConversations = true;
    this.conversationsError = null;
    this.outreach.conversations(this.modeFilter || undefined).subscribe({
      next: ({ conversations }) => {
        this.loadingConversations = false;
        this.conversations = conversations;
      },
      error: (err) => {
        this.loadingConversations = false;
        this.conversationsError = outreachError(err, 'Could not load the conversations');
      },
    });
  }

  openConversation(id: number): void {
    this.router.navigate(['/admin/people/outreach/conversations', id]);
  }

  causeLabel(id: string): string {
    return this.overview?.causes.find((c) => c.id === id)?.label ?? id;
  }

  freezeLabel(reason: string | null): string {
    return reason ? FREEZE_LABELS[reason] ?? reason : '';
  }

  /** From the intel tab: hand the item to the copilot as a prefilled message. */
  askCopilot(text: string): void {
    this.prefill = { text, at: Date.now() };
    this.show('copilot');
  }
}
